/**
 * Bounded-parallelism helper for fan-out fetches.
 *
 * Fleet pages fetch one request per boat. Firing them all at once oversubscribes the file server's
 * DuckDB connection pool (DUCKDB_POOL_SIZE, default 4), so the losers queue server-side and can hit the
 * query timeout - which surfaces as a boat silently missing from the page. Queue client-side instead.
 */

/**
 * Run `tasks` with at most `limit` in flight, preserving result order.
 * A task that rejects rejects the whole call - catch inside the task when partial results are wanted.
 */
export async function runWithConcurrency<T>(
  tasks: Array<() => Promise<T>>,
  limit: number
): Promise<T[]> {
  if (tasks.length === 0) return [];
  const results: T[] = new Array(tasks.length);
  let nextIndex = 0;
  const workerCount = Math.min(Math.max(1, limit), tasks.length);
  await Promise.all(
    Array.from({ length: workerCount }, async () => {
      while (nextIndex < tasks.length) {
        const idx = nextIndex++;
        results[idx] = await tasks[idx]();
      }
    })
  );
  return results;
}

/**
 * Default fan-out width for per-source fleet fetches. Matches the server's default DuckDB pool size so
 * requests wait in the browser rather than timing out in the pool's wait queue.
 */
export const FLEET_SOURCE_FETCH_CONCURRENCY = 4;

/**
 * Serialize calls through at most `limit` concurrent runs, queueing the rest in arrival order.
 *
 * Unlike runWithConcurrency this is long-lived and shared: callers arriving at different times from
 * different components still contend for the same slots. Use it to respect a server-side cap that no
 * single call site can see.
 */
export function createGate(limit: number): <T>(fn: () => Promise<T>) => Promise<T> {
  const max = Math.max(1, limit);
  let active = 0;
  const waiting: Array<() => void> = [];

  return async function runGated<T>(fn: () => Promise<T>): Promise<T> {
    // `while`, not `if`: a woken waiter must re-check, since a caller arriving synchronously between the
    // release and the waiter's microtask would otherwise let us exceed the limit.
    while (active >= max) {
      await new Promise<void>((resolve) => waiting.push(resolve));
    }
    active++;
    try {
      return await fn();
    } finally {
      active--;
      waiting.shift()?.();
    }
  };
}
